import React, {useMemo} from "react";
import {Game, IGameDescription} from "../../types";
import T from "../../i18n/Text";
import GameOptionsIconList from "../GameOptionsIconList";
import Button from "../Button";
import InstructionIcon from "./icons/instructions.png";
import VariationsIcon from "./icons/variations.png";
import VideoIcon from "./icons/video.png";
import Icon from "../Icon";
import YouTube, { YouTubeProps } from 'react-youtube';
import {useScrollPosition, useSize} from "../../hooks";
import {GAMES} from "../../utils/games";
import SimilarGameItem from "./components/similar-game-item/SimilarGameItem";
import "./GameInfoComponent.css"

type FVoid = () => void;

type Props = {
  game: Game;
  description: IGameDescription;
  nextClick?: FVoid;
};

enum DescriptionBlockType {
    PARAGRAPH,
    DOT,
    NUMBER
}

export const GameInfoComponent: React.FC<Props> = ({ game, description , nextClick}) => {
    const {width} = useSize();
    const videoWidth = useMemo(() => {
        const w = Math.min(width, 1440);
        return w - (w > 640 ? 100 : 20) * 2;
    }, [width]);

    const ctaOffset = useScrollPosition("ctaBlock", "pageContentContainer");

    const ctaClass = useMemo(() => {
        if (ctaOffset < 0) return "stick-to-top";

        const headerHeight = document.querySelector("header")?.offsetHeight || 0;
        const footerHeight = document.querySelector("footer")?.offsetHeight || 0;
        const element:any = document.querySelector("#ctaBlock");
        const elementHeight = element?.offsetHeight || 0;
        const windowHeight = window.innerHeight;

        const visiblePagePos = headerHeight + ctaOffset + footerHeight + elementHeight;

        if (visiblePagePos > windowHeight) return "stick-to-bottom";

        return "";
    }, [ctaOffset]);

    return getGameDescription(game, description, videoWidth, nextClick, ctaClass);
};

const getGameDescription = (game:Game, description: IGameDescription, videoWidth: number, nextClick?: FVoid, ctaClass?:string) => {
    return <div className="w-full overflow-x-hidden">
        <h2 className="text-[36px] font-bold leading-[44px] text-center my-[29px] md:my-[38px]">
            <T path={`games.${game.name}.name`} />
        </h2>
        <div>
            {getImageBlock(game)}
            {getTextBlock('games.common.infoSection.description', DescriptionBlockType.PARAGRAPH, description.description)}
            {getTextBlock('games.common.infoSection.instructions', DescriptionBlockType.NUMBER, description.instruction, InstructionIcon)}
            {nextClick && getPlayCTA(nextClick, ctaClass)}
            {getVideosBlock(description.videos, videoWidth)}
            {getTextBlock('games.common.infoSection.variations', DescriptionBlockType.DOT, description.variations, VariationsIcon)}
            {getSimilarGamesBlock(game)}
        </div>
    </div>
};

const getImageBlock = (game: Game) => {
    return <div className="md:float-left md:mr-6 mb-6">
        <img className="mx-auto" src={`/images/games/${game.name}.png`} alt={game.name}/>
        <div className="mt-4">
            <GameOptionsIconList game={game} />
        </div>
    </div>
};

const getDescriptionBlock = (titlePath: string, content: any, icon?: any) => {
    const iconImg = icon ? <Icon src={icon} alt={titlePath}/> : null;
    return <div className="mb-[40px] text-[20px] leading-[34px] w-full">
        <h3 className="mb-[20px]">
            {iconImg}
            <T path={titlePath} />
        </h3>
        {content}
    </div>
};

const getTextBlock = (titlePath: string, type: DescriptionBlockType, texts: string[], icon?: any) => {
    if (!texts || texts.length === 0) return null;

    const textWrapper = (t: DescriptionBlockType) => (content: any) => {
        return t === DescriptionBlockType.PARAGRAPH
            ? <span>{content}</span>
            : (t === DescriptionBlockType.DOT
                ? <ul className="list-disc list-outside ml-6">{content}</ul>
                : <ol className="list-decimal list-outside ml-6">{content}</ol>);
    };

    const textItem = (t: DescriptionBlockType) => (text: string, i: number) => {
        return t === DescriptionBlockType.PARAGRAPH
            ? <p key={`item-${i}`}>{text}</p>
            : <li key={`item-${i}`} className="mb-2">{text}</li>;
    };

    const content = textWrapper(type)(
        texts.map((text, i) => textItem(type)(text, i))
    );

    return getDescriptionBlock(titlePath, content, icon);
};

const getVideosBlock = (videos: string[], videoWidth: number) => {
    if (!videos || videos.length === 0) return null;

    const opts: YouTubeProps['opts'] = {
        height: Math.round(videoWidth/64*39) + '',
        width: videoWidth + ''
    };

    const content = <div id="videosBlock">
        <YouTube videoId={videos[Math.floor(Math.random() * videos.length)]} opts={opts} />
    </div>;

    return getDescriptionBlock('games.common.infoSection.video', content, VideoIcon);
};

const getSimilarGamesBlock = (game: Game) => {
    const gamesList = GAMES.filter(g => g.name !== game.name).map(g => <SimilarGameItem key={`g-${g.name}`} game={g}/>)
    const content = <div className="flex flex-row overflow-x-scroll gap-[20px]">
        {gamesList}
    </div>;
    return getDescriptionBlock('games.common.infoSection.similarGames', content);
};

const getPlayCTA = (nextClick: FVoid, ctaClass?: string) => {
    return <div id="ctaBlock" className={`cta-block ${ctaClass}`}>
        <div className="cta-wrapper">
            <Button type="primary" name="game-info-next" onClick={nextClick} className="block mx-auto"><T path="games.buttons.play"/></Button>
        </div>
    </div>
};
